/**
 * The closing, month by month, from the LAST successful run of each month.
 * The account picker is empty by default: the chart then shows the
 * cumulative of every harvested account; pick an account to see its own
 * figures. The activity toggle shows the three activities side by side
 * (All), one of them, or their Total. The closing calendar sits underneath.
 *
 * Hovering a month lists every upload launched for it with its precise
 * time; clicking a bar or a calendar month opens the month's drill-down
 * below: its uploads in order and the accounts that changed between them.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, type MonthRun, type MonthlyStats } from "../lib/api";
import { useApiQuery } from "../hooks/useApiQuery";
import {
  ALL_ACTIVITIES,
  activityChoices,
  chartRows,
  compactAmount,
  fullAmount,
  hasFigures,
  pointAt,
  seriesFor,
  uploadsOf,
} from "../lib/monthly";
import { formatDateTime, formatDateTimeSeconds } from "../lib/runs";
import { resolveText } from "../lib/tooltext";
import { categoryOf } from "../lib/states";
import { EmptyState } from "./ui/EmptyState";
import { ErrorAlert } from "./ui/ErrorAlert";
import { MonthChanges } from "./MonthChanges";

const CALENDAR_COLOR: Record<string, "default" | "success" | "warning" | "error" | "info"> = {
  success: "success",
  warning: "warning",
  failure: "error",
  waiting: "warning",
  running: "info",
  queued: "info",
};

/** How many uploads the hover card lists before "+n". */
const TOOLTIP_UPLOADS = 6;

type TooltipItem = {
  name?: string | number;
  value?: unknown;
  color?: string;
  payload?: { period?: number; month?: string };
};

export function MonthlyFigures() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [year, setYear] = useState<number | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [activity, setActivity] = useState<string>(ALL_ACTIVITIES);
  // the month whose uploads and account changes are opened underneath
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(null);

  const query = useApiQuery(["stats", "monthly", year, account], (signal) =>
    api.monthly(year ?? undefined, account, signal),
  );
  const stats: MonthlyStats | null = query.data ?? null;

  const rows = useMemo(
    () => (stats ? chartRows(stats, activity, i18n.language) : []),
    [stats, activity, i18n.language],
  );
  const seriesColor: Record<string, string> = {
    CEM: theme.tokens.chart.runs,
    RMC: theme.tokens.chart.uploads,
    AGG: theme.palette.warning.main,
  };
  const tick = { fontSize: 12, fill: theme.palette.text.secondary };

  if (!stats) {
    if (query.isError) {
      return <ErrorAlert error={query.error} onRetry={() => void query.refetch()} />;
    }
    return <LinearProgress />;
  }

  const carried = seriesFor(stats, activity);
  const selected = stats.accounts.find((a) => a.fs_item === stats.fs_item) ?? null;
  const choiceLabel = (name: string) =>
    name === ALL_ACTIVITIES
      ? t("monthly.all")
      : name === stats.total_series
        ? t("monthly.total")
        : name;
  const stateLabel = (state: string) => t(`states.${state}`, { defaultValue: state });
  const openMonth = (period: number) => {
    if (Number.isInteger(period) && period >= 1 && period <= 12) setSelectedPeriod(period);
  };
  const changeYear = (next: number) => {
    setYear(next);
    setSelectedPeriod(null);
  };

  /** The hover card: the month's figures, then every upload with its precise time. */
  const renderTooltip = (props: { active?: boolean; payload?: readonly TooltipItem[]; label?: unknown }) => {
    if (!props.active || !props.payload?.length) return null;
    const period = Number(props.payload[0]?.payload?.period);
    const uploads = uploadsOf(stats, period);
    return (
      <Paper elevation={3} sx={{ p: 1.5, maxWidth: 360 }}>
        <Typography variant="subtitle2" gutterBottom>
          {String(props.label ?? "")}
        </Typography>
        {props.payload.map((item) => {
          const series = String(item.name);
          const point = pointAt(stats, series, period);
          return (
            <Stack key={series} direction="row" spacing={1} alignItems="baseline">
              <Box
                component="span"
                sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: item.color, flex: "0 0 auto" }}
              />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {choiceLabel(series)}
              </Typography>
              <Typography variant="body2" className="num">
                {fullAmount(Number(item.value), i18n.language, point?.currency)}
              </Typography>
            </Stack>
          );
        })}
        {(() => {
          const source = props.payload
            .map((item) => pointAt(stats, String(item.name), period)?.source)
            .find((s) => s);
          return source ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {t("monthly.figuresSource", {
                tool: resolveText(source.display_name, i18n.language) || source.name,
              })}
            </Typography>
          ) : null;
        })()}
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {uploads.length === 0
            ? t("monthly.noUpload")
            : t("monthly.uploads", { count: uploads.length })}
        </Typography>
        {uploads.slice(0, TOOLTIP_UPLOADS).map((run: MonthRun) => (
          <Typography key={run.job_id} variant="caption" sx={{ display: "block" }}>
            <span className="num">{formatDateTimeSeconds(run.run_at, i18n.language)}</span>
            {" · "}
            {run.file_name ?? t("monthly.noFile")}
            {" · "}
            {stateLabel(run.state)}
          </Typography>
        ))}
        {uploads.length > TOOLTIP_UPLOADS && (
          <Typography variant="caption" color="text.secondary">
            +{uploads.length - TOOLTIP_UPLOADS}
          </Typography>
        )}
        <Typography variant="caption" color="primary" sx={{ display: "block", mt: 1 }}>
          {t("monthly.clickHint")}
        </Typography>
      </Paper>
    );
  };

  return (
    <>
      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6">{t("monthly.title")}</Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.scope === "account"
                  ? t("monthly.subtitleAccount", {
                      account: `${stats.fs_item}${stats.account_label ? ` ${stats.account_label}` : ""}`,
                    })
                  : t("monthly.subtitleAll", { count: stats.accounts.length })}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="monthly-year">{t("monthly.year")}</InputLabel>
                <Select
                  labelId="monthly-year"
                  label={t("monthly.year")}
                  value={String(stats.year)}
                  onChange={(e) => changeYear(Number(e.target.value))}
                >
                  {stats.years.map((y) => (
                    <MenuItem key={y} value={String(y)}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={activity}
                onChange={(_e, next: string | null) => next && setActivity(next)}
                aria-label={t("monthly.activity")}
              >
                {activityChoices(stats).map((name) => (
                  <ToggleButton key={name} value={name}>
                    {choiceLabel(name)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Autocomplete
                size="small"
                sx={{ minWidth: 320, maxWidth: 480 }}
                options={stats.accounts}
                value={selected}
                disabled={stats.accounts.length === 0}
                getOptionLabel={(a) => `${a.fs_item} — ${a.label}`}
                isOptionEqualToValue={(a, b) => a.fs_item === b.fs_item}
                // clearing returns to the cumulative of every account
                onChange={(_e, next) => setAccount(next?.fs_item ?? null)}
                renderInput={(params) => <TextField {...params} label={t("monthly.account")} />}
              />
            </Stack>
          </Stack>

          {!hasFigures(stats, activity) ? (
            <EmptyState message={t("monthly.empty")} />
          ) : (
            <Box
              role="img"
              aria-label={`${t("monthly.chartLabel", { series: choiceLabel(activity) })} — ${t("monthly.chartClickHint")}`}
            >
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={rows}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                  <XAxis dataKey="month" tick={tick} />
                  <YAxis
                    tick={tick}
                    width={70}
                    tickFormatter={(v: number) => compactAmount(v, i18n.language)}
                  />
                  <ChartTooltip content={renderTooltip} />
                  {carried.length > 1 && <Legend formatter={(value: string) => choiceLabel(value)} />}
                  {carried.map((series) => (
                    <Bar
                      key={series}
                      dataKey={series}
                      name={series}
                      fill={seriesColor[series] ?? theme.palette.primary.main}
                      radius={[3, 3, 0, 0]}
                      cursor="pointer"
                      onClick={(data: unknown) => {
                        // recharts hands the chart row merged into the clicked bar
                        const item = data as { period?: number; payload?: { period?: number } };
                        openMonth(Number(item.payload?.period ?? item.period));
                      }}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          <Typography variant="overline" color="text.secondary" sx={{ mt: 2, display: "block" }}>
            {t("monthly.calendar", { year: stats.year })}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {stats.calendar.map((month) => {
              const label = rows[month.period - 1]?.month ?? String(month.period);
              const category = month.state ? categoryOf(month.state) : null;
              const isSelected = month.period === selectedPeriod;
              // a backend older than this screen answers without the list
              const uploads = month.runs ?? [];
              const chip = (
                <Chip
                  size="small"
                  label={label}
                  variant={month.state ? "filled" : "outlined"}
                  color={category ? (CALENDAR_COLOR[category] ?? "default") : "default"}
                  onClick={uploads.length > 0 ? () => openMonth(month.period) : undefined}
                  sx={{
                    minWidth: 52,
                    outline: isSelected ? `2px solid ${theme.palette.primary.main}` : "none",
                    outlineOffset: 1,
                  }}
                />
              );
              return (
                <Tooltip
                  key={month.period}
                  title={
                    month.state
                      ? `${stateLabel(month.state)} · ${formatDateTime(month.run_at, i18n.language)} · ${t("monthly.uploads", { count: uploads.length })}`
                      : t("monthly.noRun")
                  }
                >
                  {chip}
                </Tooltip>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {selectedPeriod != null && (
        <MonthChanges
          key={`${stats.year}-${selectedPeriod}`}
          year={stats.year}
          period={selectedPeriod}
          onClose={() => setSelectedPeriod(null)}
        />
      )}
    </>
  );
}
